import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { BrandSiteReleasePreviewDialog } from "@/components/brand-site/BrandSiteReleasePreviewDialog";
import { HermesDrawer } from "@/components/hermes/HermesDrawer";
import { useHermes } from "@/components/hermes/HermesProvider";
import { AdminAlert, AdminEmptyState, AdminPageHeader } from "@/components/shell/AdminPage";
import { ConfirmDialog } from "@/components/shell/ConfirmDialog";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  brandSiteScoreOptimizeSeedPrompt,
  shouldAcceptBrandSiteDraftPreview,
  showBrandSiteScoreOptimizeEntry,
} from "@/lib/hermes-brand-site-score-optimize";
import { useBrandSiteWorkbench } from "@/pages/brand-site/BrandSiteSiteLayout";
import {
  BrandSiteApiError,
  brandSiteAgentScoreCopy,
  brandSiteDraftScoreCopy,
  brandSiteReleaseScoreLabel,
  brandSiteStatusLabel,
  brandSiteTooFewPagesCopy,
  brandSiteVisitUrl,
  getBrandSite,
  getBrandSiteRelease,
  listBrandSiteReleases,
  previewBrandSiteAgentScore,
  publishBrandSite,
  rollbackBrandSiteRelease,
  type BrandSite,
  type BrandSiteAgentScorePreview,
  type BrandSiteReleaseListItem,
  type BrandSiteReleasePage,
} from "@/lib/brand-site-api";

type PreviewState = { release: BrandSiteReleaseListItem } | null;

/** 发布台：把草稿打成正式版；可预览历史正式版并在预览中回滚 */
export default function BrandSitePublishPage() {
  const { brandId } = useParams();
  const { accountId, brands } = useAccountWorkspace();
  const brand = brands.find((item) => String(item.id) === brandId);
  const { currentSite, reload } = useBrandSiteWorkbench();
  const {
    startBrandSiteScoreOptimizeSession,
    stopBrandSiteScoreOptimize,
    restartBrandSiteScoreOptimizeSession,
  } = useHermes();
  const editPath = brandId
    ? `/accounts/${accountId}/brands/${brandId}/apps/brand_site/sites/${currentSite.id}/edit`
    : "";

  const [site, setSite] = useState<BrandSite | null>(null);
  const [releases, setReleases] = useState<BrandSiteReleaseListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [openingScoreOptimize, setOpeningScoreOptimize] = useState(false);
  const [restartingScoreOptimize, setRestartingScoreOptimize] = useState(false);
  const [scoreOptimizeOpen, setScoreOptimizeOpen] = useState(false);
  const [draftPreview, setDraftPreview] = useState<Pick<
    BrandSiteAgentScorePreview,
    "score" | "grade" | "capped"
  > | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [dialog, setDialog] = useState<PreviewState>(null);
  const [dialogPages, setDialogPages] = useState<BrandSiteReleasePage[] | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackWarnOpen, setRollbackWarnOpen] = useState(false);
  const currentSiteIdRef = useRef(currentSite.id);
  currentSiteIdRef.current = currentSite.id;
  const draftPreviewGenerationRef = useRef(0);

  const loadReleases = useCallback(
    async (siteId: number) => {
      try {
        const payload = await listBrandSiteReleases(accountId, siteId);
        setReleases(payload.releases);
      } catch {
        setReleases([]);
      }
    },
    [accountId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await getBrandSite(accountId, currentSite.id);
      setSite(payload.brand_site);
      if (payload.brand_site.status === "published") {
        await loadReleases(payload.brand_site.id);
      } else {
        setReleases([]);
      }
    } catch (caught) {
      setError(caught instanceof BrandSiteApiError ? caught.message : "暂时没法打开，请稍后重试");
      setSite(null);
      setReleases([]);
    } finally {
      setLoading(false);
    }
  }, [accountId, currentSite.id, loadReleases]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    draftPreviewGenerationRef.current += 1;
    setDraftPreview(null);
    setPreviewing(false);
    setOpeningScoreOptimize(false);
    setScoreOptimizeOpen(false);
    stopBrandSiteScoreOptimize();
  }, [currentSite.id, stopBrandSiteScoreOptimize]);

  useEffect(
    () => () => {
      stopBrandSiteScoreOptimize();
    },
    [stopBrandSiteScoreOptimize],
  );

  const canPublish = site?.status === "draft_ready" || site?.status === "published";

  async function handlePublish() {
    if (!site) return;
    setPublishing(true);
    setError(null);
    try {
      const payload = await publishBrandSite(accountId, site.id);
      setSite(payload.brand_site);
      setDraftPreview(null);
      await loadReleases(payload.brand_site.id);
      await reload();
      toast.success("已发布。若仍像旧站，点「打开网站（看新版）」或换浏览器试试");
    } catch (caught) {
      const message = caught instanceof BrandSiteApiError ? caught.message : "发布失败，请稍后重试";
      setError(message);
      toast.error(message);
    } finally {
      setPublishing(false);
    }
  }

  async function handlePreviewDraft() {
    if (!site) return;
    const requestedSiteId = site.id;
    const requestedGeneration = ++draftPreviewGenerationRef.current;
    setPreviewing(true);
    try {
      const result = await previewBrandSiteAgentScore(accountId, requestedSiteId);
      if (
        !shouldAcceptBrandSiteDraftPreview(
          currentSiteIdRef.current,
          draftPreviewGenerationRef.current,
          requestedSiteId,
          requestedGeneration,
        )
      ) {
        return;
      }
      setDraftPreview({ score: result.score, grade: result.grade, capped: result.capped });
    } catch (caught) {
      if (
        !shouldAcceptBrandSiteDraftPreview(
          currentSiteIdRef.current,
          draftPreviewGenerationRef.current,
          requestedSiteId,
          requestedGeneration,
        )
      ) {
        return;
      }
      const message =
        caught instanceof BrandSiteApiError ? caught.message : "预评失败，请稍后重试";
      toast.error(message);
    } finally {
      if (
        shouldAcceptBrandSiteDraftPreview(
          currentSiteIdRef.current,
          draftPreviewGenerationRef.current,
          requestedSiteId,
          requestedGeneration,
        )
      ) {
        setPreviewing(false);
      }
    }
  }

  const dialogReleaseId = dialog?.release.id ?? null;

  useEffect(() => {
    if (!dialog || !site || dialogReleaseId == null) {
      setDialogPages(null);
      setDialogError(null);
      setDialogLoading(false);
      return;
    }

    let cancelled = false;
    setDialogLoading(true);
    setDialogError(null);
    setDialogPages(null);

    void getBrandSiteRelease(accountId, site.id, dialogReleaseId)
      .then((payload) => {
        if (cancelled) return;
        setDialogPages(payload.pages);
      })
      .catch((caught) => {
        if (cancelled) return;
        setDialogError(caught instanceof BrandSiteApiError ? caught.message : "预览加载失败，请稍后重试");
        setDialogPages(null);
      })
      .finally(() => {
        if (!cancelled) setDialogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId, dialog, dialogReleaseId, site]);

  async function handleRollbackConfirm() {
    if (!site || !dialog || dialog.release.current) return;
    setRollingBack(true);
    try {
      const payload = await rollbackBrandSiteRelease(accountId, site.id, dialog.release.id);
      setSite(payload.brand_site);
      await loadReleases(payload.brand_site.id);
      await reload();
      setRollbackWarnOpen(false);
      setDialog(null);
      toast.success(`已回滚到第 ${payload.release.number} 版。访客现在会看到这一版。`);
    } catch (caught) {
      const message = caught instanceof BrandSiteApiError ? caught.message : "回滚失败，请稍后重试";
      toast.error(message);
    } finally {
      setRollingBack(false);
    }
  }

  const publishedAt = site?.current_release?.published_at
    ? new Date(site.current_release.published_at).toLocaleString("zh-CN")
    : null;
  const scoreCopy = site ? brandSiteAgentScoreCopy(site) : null;
  const draftScoreCopy = brandSiteDraftScoreCopy(draftPreview);
  const showScoreOptimizeEntry = showBrandSiteScoreOptimizeEntry(
    site?.agent_score,
    draftPreview?.score,
  );
  const tooFewPagesCopy = site ? brandSiteTooFewPagesCopy(site) : null;
  const platformVisitUrl = site ? brandSiteVisitUrl(site) : null;
  const customVisitUrl =
    site?.custom_host?.trim() ? brandSiteVisitUrl(site, { preferCustomHost: true }) : null;

  async function openScoreOptimize() {
    if (!site || !brandId) return;
    if (loading || site.id !== currentSite.id) return;
    const requestedSiteId = site.id;
    setOpeningScoreOptimize(true);
    const result = await startBrandSiteScoreOptimizeSession(
      {
        mode: "brand_site_score_optimize",
        accountId,
        brandId: Number(brandId),
        brandSiteId: requestedSiteId,
      },
      {
        composerSeed: brandSiteScoreOptimizeSeedPrompt({
          siteTitle: site.title,
          siteScore: site.agent_score,
          siteGrade: site.agent_grade,
          draftScore: draftPreview?.score,
          draftGrade: draftPreview?.grade,
        }),
      },
    );
    if (currentSiteIdRef.current !== requestedSiteId) {
      setOpeningScoreOptimize(false);
      return;
    }
    setOpeningScoreOptimize(false);
    if (!result.syncFailed) setScoreOptimizeOpen(true);
  }

  async function restartScoreOptimize() {
    if (!site || !brandId) return;
    setRestartingScoreOptimize(true);
    try {
      await restartBrandSiteScoreOptimizeSession({
        composerSeed: brandSiteScoreOptimizeSeedPrompt({
          siteTitle: site.title,
          siteScore: site.agent_score,
          siteGrade: site.agent_grade,
          draftScore: draftPreview?.score,
          draftGrade: draftPreview?.grade,
        }),
      });
    } finally {
      setRestartingScoreOptimize(false);
    }
  }

  const currentRelease =
    releases.find((item) => item.current) ??
    (site?.current_release
      ? {
          ...site.current_release,
          current: true,
        }
      : null);

  const dialogTitle = dialog ? (
    <>
      <span>第 {dialog.release.number} 版</span>
      <span className="text-sm font-normal text-muted-foreground">
        {formatReleaseWhen(dialog.release.published_at)}
      </span>
    </>
  ) : null;

  const dialogDescription = dialog ? (
    (() => {
      const parts: string[] = [];
      if (dialog.release.current) parts.push("当前正式版");
      const score = brandSiteReleaseScoreLabel(dialog.release);
      if (score) parts.push(`智能体可读性 ${score}`);
      if (parts.length === 0) return null;
      return <span className="text-muted-foreground">{parts.join(" · ")}</span>;
    })()
  ) : null;

  const previewVersionNav = useMemo(() => {
    if (!dialog || releases.length < 2) return undefined;
    const index = releases.findIndex((item) => item.id === dialog.release.id);
    if (index < 0) return undefined;
    const older = releases[index + 1];
    const newer = releases[index - 1];
    return {
      hasPrevious: older != null,
      hasNext: newer != null,
      onPrevious: () => {
        setRollbackWarnOpen(false);
        if (older) setDialog({ release: older });
      },
      onNext: () => {
        setRollbackWarnOpen(false);
        if (newer) setDialog({ release: newer });
      },
    };
  }, [dialog, releases]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="发布"
        description={brand ? `发布后，访客就能打开「${brand.name}」的网址。` : "发布后访客就能打开这个网址。"}
      />

      {loading ? <p className="text-sm text-muted-foreground">加载中…</p> : null}
      {error ? <AdminAlert>{error}</AdminAlert> : null}

      {site && !canPublish ? (
        <AdminEmptyState>
          还没有可发布的草稿。先去
          <Link to={editPath} className="mx-1 text-primary-readable hover:underline">
            做一版官网
          </Link>
          。
        </AdminEmptyState>
      ) : null}

      {site && canPublish ? (
        <div className="space-y-4 rounded-lg border border-border bg-card px-5 py-4">
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">状态</dt>
              <dd>{brandSiteStatusLabel(site.status)}</dd>
            </div>
            {publishedAt ? (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">上次发布</dt>
                <dd>
                  第 {site.current_release?.number} 版 · {publishedAt}
                </dd>
              </div>
            ) : null}
            {scoreCopy ? (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">智能体可读性</dt>
                <dd className="max-w-[16rem] text-right">
                  <div>{scoreCopy.title.replace(/^智能体可读性\s+/, "")}</div>
                  {scoreCopy.jsonLdHint ? (
                    <p className="mt-1 text-xs text-muted-foreground">{scoreCopy.jsonLdHint}</p>
                  ) : null}
                  {scoreCopy.hint ? (
                    <p className="mt-1 text-xs text-muted-foreground">{scoreCopy.hint}</p>
                  ) : null}
                </dd>
              </div>
            ) : null}
            {draftScoreCopy ? (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">草稿预评</dt>
                <dd className="max-w-[16rem] text-right">
                  <div>{draftScoreCopy.title.replace(/^草稿预评\s+/, "")}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{draftScoreCopy.hint}</p>
                </dd>
              </div>
            ) : null}
          </dl>

          {tooFewPagesCopy ? <AdminAlert tone="info">{tooFewPagesCopy}</AdminAlert> : null}

          {site.status === "published" ? (
            <AdminAlert tone="warning" role="status">
              <span className="block font-medium">刚发布若仍像旧站</span>
              <span className="mt-1 block opacity-90">
                浏览器可能暂时缓存了旧页面。可以等约 5 分钟再刷新，或换个浏览器 /
                无痕窗口试试；要马上核对，请点下方「打开网站（看新版）」链接（地址里带版本参数，会绕过旧缓存）。
              </span>
            </AdminAlert>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" onClick={() => void handlePublish()} disabled={publishing}>
              {publishing ? "发布中…" : site.status === "published" ? "重新发布" : "发布"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => void handlePreviewDraft()}
              disabled={previewing || publishing}
            >
              {previewing ? "预评中…" : "预评草稿"}
            </Button>
            {showScoreOptimizeEntry ? (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                disabled={
                  openingScoreOptimize || loading || (site != null && site.id !== currentSite.id)
                }
                onClick={() => void openScoreOptimize()}
              >
                <Sparkles className="size-4" aria-hidden />
                {openingScoreOptimize ? "正在打开…" : "打开可读性优化"}
              </Button>
            ) : null}
            {site.status === "published" && platformVisitUrl ? (
              <a
                href={platformVisitUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary-readable hover:underline"
              >
                打开网站（看新版）
              </a>
            ) : null}
            {site.status === "published" && customVisitUrl ? (
              <a
                href={customVisitUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-primary-readable hover:underline"
              >
                打开自定义域名（看新版）
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {site?.status === "published" && releases.length > 0 ? (
        <section className="space-y-3 rounded-lg border border-border bg-card px-5 py-4">
          <div>
            <h2 className="text-sm font-medium">历史正式版</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              先点「查看」打开任一版；若不是当前正式版，可在顶栏回滚（不会复制整站，只切换当前正式版）。
            </p>
          </div>
          <ul className="divide-y divide-border rounded-md border border-border">
            {releases.map((release) => {
              const when = formatReleaseWhen(release.published_at);
              const scoreLabel = brandSiteReleaseScoreLabel(release);
              return (
                <li
                  key={release.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 text-sm"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">第 {release.number} 版</span>
                      {release.current ? (
                        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary-readable">
                          当前
                        </span>
                      ) : null}
                      {scoreLabel ? (
                        <span
                          className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                          title="智能体可读性（仅参考，不挡住发布）"
                        >
                          {scoreLabel}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">暂无评分</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{when}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDialog({ release })}
                  >
                    查看
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <BrandSiteReleasePreviewDialog
        open={dialog != null}
        onOpenChange={(open) => {
          if (!open && !rollingBack) {
            setRollbackWarnOpen(false);
            setDialog(null);
          }
        }}
        title={dialogTitle}
        description={dialogDescription}
        pages={dialogPages}
        loading={dialogLoading}
        error={dialogError}
        versionNav={previewVersionNav}
        confirm={
          dialog && !dialog.release.current
            ? {
                label: `回滚到第 ${dialog.release.number} 版`,
                tone: "destructive",
                confirming: rollingBack,
                onConfirm: () => setRollbackWarnOpen(true),
              }
            : undefined
        }
      />

      <Sheet
        open={scoreOptimizeOpen}
        onOpenChange={(open) => {
          setScoreOptimizeOpen(open);
          if (!open) stopBrandSiteScoreOptimize();
        }}
      >
        <SheetContent className="w-full gap-0 p-0 sm:max-w-xl">
          <SheetHeader className="shrink-0 border-b border-border px-5 py-4 pr-28 text-left">
            <SheetTitle>可读性优化</SheetTitle>
            <SheetDescription>
              Hermes 会先检查草稿、给出计划；你确认后才会改页面。
            </SheetDescription>
          </SheetHeader>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute top-4 right-14 z-10 h-7 gap-1 px-2 text-xs"
            disabled={restartingScoreOptimize}
            aria-label="新对话"
            onClick={() => void restartScoreOptimize()}
          >
            <Plus className="size-3.5" />
            {restartingScoreOptimize ? "新建中…" : "新对话"}
          </Button>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <HermesDrawer variant="embedded" />
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={rollbackWarnOpen && dialog != null && !dialog.release.current}
        onOpenChange={(open) => {
          if (!rollingBack) setRollbackWarnOpen(open);
        }}
        tone="destructive"
        title={dialog ? `确认回滚到第 ${dialog.release.number} 版？` : "确认回滚？"}
        description={
          dialog ? (
            <div className="space-y-2 text-sm">
              <p>
                访客会立刻看到第 {dialog.release.number} 版
                {currentRelease ? `（不再是第 ${currentRelease.number} 版）` : ""}。
              </p>
              <p className="text-muted-foreground">
                不会删掉任何历史正式版，只是把「当前正式版」指针切过去；之后仍可再回滚到别的版。
              </p>
            </div>
          ) : null
        }
        confirmLabel="确认回滚"
        cancelLabel="再想想"
        confirming={rollingBack}
        onConfirm={() => void handleRollbackConfirm()}
      />
    </div>
  );
}

function formatReleaseWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("zh-CN");
}
